/**
 * annotationAdapter.ts — bridges the app's existing per-key annotation maps
 * (built today inside DocumentWithReviewPanel from ursOfferNotesSlice,
 * importedDocDepartmentsSlice and importedDocLinksSlice) into the
 * AnnotationLookup shape the segmenter consumes.
 *
 * Pure function, no Redux — so it can be unit-tested and reused by whatever
 * container wires the static renderer into the screen.
 *
 * NOTE: discussion-thread counts (`commentCounts` prop in the old panel) are
 * intentionally not mapped yet — they need a product decision on how a
 * comment-only-by-count block should appear in the gutter. Tracked as a
 * follow-up before retiring the old path.
 */

import { AnnotationData, AnnotationLookup, NoteCode } from "./segmenter";

export interface AnnotationSources {
    noteByKey?: Record<string, string | undefined>;
    commentsByKey?: Record<string, string | undefined>;
    deptIdByKey?: Record<string, number | null | undefined>;
    docLinkIdByKey?: Record<string, number | null | undefined>;
}

export function buildAnnotationLookup(sources: AnnotationSources): AnnotationLookup {
    const { noteByKey = {}, commentsByKey = {}, deptIdByKey = {}, docLinkIdByKey = {} } = sources;

    const keys = new Set<string>([
        ...Object.keys(noteByKey),
        ...Object.keys(commentsByKey),
        ...Object.keys(deptIdByKey),
        ...Object.keys(docLinkIdByKey),
    ]);

    const out: AnnotationLookup = {};
    for (const key of keys) {
        const entry: AnnotationData = {};

        const note = noteByKey[key];
        if (note) entry.note = note as NoteCode; // empty string "" is falsy → no note

        const comment = commentsByKey[key];
        if (comment) entry.comment = comment;

        const deptId = deptIdByKey[key];
        if (deptId != null) entry.deptId = deptId;

        const docLinkId = docLinkIdByKey[key];
        if (docLinkId != null) entry.docLinkId = docLinkId;

        // Only annotated keys make it in; the segmenter treats presence as "annotated".
        if (entry.note || entry.comment || entry.deptId != null || entry.docLinkId != null) {
            out[key] = entry;
        }
    }

    return out;
}

/**
 * Builds an AnnotationLookup directly from raw Redux slice data, mirroring the
 * per-key map logic currently inside DocumentWithReviewPanel (pending changes
 * override saved values). This is the duplicated logic that will be deleted
 * once the old JS-measured path is retired; kept pure here so it stays tested.
 */
export interface NoteRecord {
    blockKey: string | number;
    currentNote?: {
        note?: string | null;
        comment?: string | null;
        createdBy?: string | null;
        createdByFullName?: string | null;
    } | null;
}
export interface PendingNote {
    note: string;
    comment: string;
}
export interface DeptAssignment {
    blockKey: string;
    companyDepartmentId: number | null;
}
export interface DocLinkAssignment {
    blockKey: string;
    companyDocumentTypeId: number | null;
}

export interface AnnotationState {
    notes: NoteRecord[];
    notePending: Record<string, PendingNote>;
    deptAssignments: DeptAssignment[];
    deptPending: Record<string, number | null>;
    docLinkAssignments: DocLinkAssignment[];
    docLinkPending: Record<string, number | null>;
}

export function annotationLookupFromState(state: AnnotationState): AnnotationLookup {
    const noteByKey: Record<string, string | undefined> = {};
    const commentsByKey: Record<string, string | undefined> = {};
    for (const nh of state.notes) {
        const key = String(nh.blockKey);
        if (nh.currentNote?.note) noteByKey[key] = nh.currentNote.note;
        if (nh.currentNote?.comment) commentsByKey[key] = nh.currentNote.comment;
    }
    for (const [key, p] of Object.entries(state.notePending)) {
        noteByKey[key] = p.note;
        if (p.comment) commentsByKey[key] = p.comment;
    }

    const deptIdByKey: Record<string, number | null> = {};
    for (const a of state.deptAssignments) deptIdByKey[a.blockKey] = a.companyDepartmentId;
    for (const [key, id] of Object.entries(state.deptPending)) deptIdByKey[key] = id;

    const docLinkIdByKey: Record<string, number | null> = {};
    for (const a of state.docLinkAssignments) docLinkIdByKey[a.blockKey] = a.companyDocumentTypeId;
    for (const [key, id] of Object.entries(state.docLinkPending)) docLinkIdByKey[key] = id;

    return buildAnnotationLookup({ noteByKey, commentsByKey, deptIdByKey, docLinkIdByKey });
}

/**
 * Author of each block's saved comment, for the gutter bubble. Keys with a
 * pending (unsaved) change are left out — the bubble then shows text nobody has
 * saved yet, so naming the previous author would misattribute it.
 */
export function commentAuthorByKey(
    notes: NoteRecord[],
    notePending: Record<string, unknown>,
): Record<string, string> {
    const out: Record<string, string> = {};
    for (const nh of notes) {
        const key = String(nh.blockKey);
        const n = nh.currentNote;
        if (!n?.comment || key in notePending) continue;
        const author = n.createdByFullName || n.createdBy;
        if (author) out[key] = author;
    }
    return out;
}
